import React, { useState, useEffect } from 'react';
import { GenerationResponse, CasoPrueba } from '../types';
import api from '../services/api';
import { Loader2, ArrowLeft, Copy, Check, FileSpreadsheet, FileJson, AlertTriangle } from 'lucide-react';

import { useNavigate, useLocation } from 'react-router-dom';
import TestCaseCard from '../components/TestCaseCard';

const Results: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [result, setResult] = useState<GenerationResponse | null>((location.state as { result: GenerationResponse })?.result || null);

  const onBack = () => navigate('/generator');

  useEffect(() => {
    let interval: any;

    if (result && (result.status === 'processing' || result.status === 'pending')) {
      interval = setInterval(async () => {
        try {
          const updatedJob = await api.getJob(result.job_id);
          setResult(updatedJob);
          if (updatedJob.status !== 'processing' && updatedJob.status !== 'pending') {
            clearInterval(interval);
          }
        } catch (error) {
          console.error("Polling failed:", error);
          clearInterval(interval);
        }
      }, 3000); // Poll every 3 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [result?.job_id, result?.status]);

  if (!result) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-400">
        <AlertTriangle className="w-12 h-12 mb-4 text-yellow-500" />
        <p>No results found in state.</p>
        <button
          onClick={onBack}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600"
        >
          Go Back
        </button>
      </div>
    );
  }
  const [expandedCases, setExpandedCases] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [xrayContent, setXrayContent] = useState<string | null>(null);

  const casos = result.structured_data?.casos || [];

  const toggleCase = (caseId: string) => {
    const newExpanded = new Set(expandedCases);
    if (newExpanded.has(caseId)) {
      newExpanded.delete(caseId);
    } else {
      newExpanded.add(caseId);
    }
    setExpandedCases(newExpanded);
  };

  const handleCopy = async () => {
    if (result.test_cases) {
      await navigator.clipboard.writeText(result.test_cases);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloading('excel');
    try {
      const blob = await api.downloadExcel(result.job_id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `casos_prueba_${result.hu_id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadJson = async () => {
    setDownloading('json');
    try {
      const blob = await api.downloadJson(result.job_id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `casos_prueba_${result.hu_id}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading(null);
    }
  };

  // Summary
  if (result.status === 'processing' || result.status === 'pending') {
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-surface-dark border border-slate-800 rounded-2xl animate-in fade-in zoom-in duration-500">
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full"></div>
          <Loader2 className="w-16 h-16 text-primary animate-spin relative z-10" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Generando Casos de Prueba</h2>
        <p className="text-slate-400 text-center max-w-md px-6">
          Nuestra IA está analizando los requisitos y diseñando los escenarios óptimos.
          Este proceso puede tardar unos segundos.
        </p>
        <div className="mt-8 flex gap-2">
          <span className="w-2 h-2 rounded-full bg-primary animate-bounce delay-0"></span>
          <span className="w-2 h-2 rounded-full bg-primary animate-bounce delay-150"></span>
          <span className="w-2 h-2 rounded-full bg-primary animate-bounce delay-300"></span>
        </div>
      </div>
    );
  }

  if (result.status === 'failed') {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-red-900/10 border border-red-500/20 rounded-2xl text-center">
        <AlertTriangle className="w-16 h-16 text-red-500 mb-6" />
        <h2 className="text-2xl font-bold text-white mb-2">Error en la Generación</h2>
        <p className="text-red-400 max-w-lg mb-8">{result.error_message || 'Ocurrió un error inesperado al procesar la solicitud.'}</p>
        <button
          onClick={onBack}
          className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
        >
          Intentar de nuevo
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-white">
              Test Cases for {result.hu_id}
            </h2>
            <p className="text-sm text-slate-400">
              {result.num_cases} cases generated • {result.test_type} • {result.execution_time_ms}ms
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
          >
            {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
          <button
            onClick={handleDownloadExcel}
            disabled={downloading === 'excel'}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span>{downloading === 'excel' ? 'Downloading...' : 'Excel'}</span>
          </button>
          <button
            onClick={handleDownloadJson}
            disabled={downloading === 'json'}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <FileJson className="w-4 h-4" />
            <span>{downloading === 'json' ? 'Downloading...' : 'JSON'}</span>
          </button>
        </div>
      </div>

      {/* Summary */}
      {result.structured_data && (
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-2">Resumen</h3>
          <p className="text-slate-400">{result.structured_data.resumen}</p>
          <div className="mt-4 pt-4 border-t border-slate-700">
            <p className="text-sm text-slate-500">Cobertura:</p>
            <p className="text-slate-300">{result.structured_data.cobertura}</p>
          </div>
        </div>
      )}

      {/* Blocked Cases Section - V2.2 */}
      {result.structured_data?.blocked_questions && result.structured_data.blocked_questions.length > 0 && (
        <div className="bg-yellow-900/20 border border-yellow-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Casos Bloqueados ({result.structured_data.blocked_questions.length})
          </h3>
          <p className="text-sm text-slate-400 mb-4">
            Los siguientes casos no se pudieron generar por falta de evidencia suficiente en el documento
          </p>
          <div className="space-y-3">
            {result.structured_data.blocked_questions.map((bq: any, idx: number) => (
              <div key={idx} className="bg-slate-800/50 rounded-lg p-4 border border-yellow-700/30">
                <p className="text-sm font-mono text-yellow-300 mb-2 font-semibold">{bq.missing_for}</p>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs text-slate-500 uppercase tracking-wide">Pregunta:</span>
                    <p className="text-slate-300 mt-1">{bq.question}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 uppercase tracking-wide">Por qué se necesita:</span>
                    <p className="text-sm text-slate-400 mt-1">{bq.why_needed}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 uppercase tracking-wide">Fuente sugerida:</span>
                    <p className="text-xs text-slate-500 mt-1">{bq.suggested_source}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cases List */}
      <div className="space-y-4">
        {casos.map((caso: CasoPrueba) => (
          <TestCaseCard
            key={caso.id_caso}
            caso={caso}
            isExpanded={expandedCases.has(caso.id_caso)}
            onToggle={() => toggleCase(caso.id_caso)}
            onShowXray={setXrayContent}
          />
        ))}
      </div>

      {/* Raw Output (Collapsed) */}
      {result.test_cases && (
        <details className="bg-surface-dark border border-slate-800 rounded-xl overflow-hidden">
          <summary className="px-6 py-4 cursor-pointer hover:bg-slate-800/50 transition-colors">
            <span className="text-white font-medium">Raw Output</span>
          </summary>
          <pre className="p-6 bg-slate-900 text-slate-300 text-sm overflow-x-auto whitespace-pre-wrap max-h-96">
            {result.test_cases}
          </pre>
        </details>
      )}

      {/* X-Ray Side Panel */}
      {xrayContent && (
        <div className="fixed inset-y-0 right-0 w-96 bg-surface-dark border-l border-slate-700 shadow-2xl p-6 transform transition-transform duration-300 ease-in-out z-50 overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary"></span>
              Context X-Ray
            </h3>
            <button
              onClick={() => setXrayContent(null)}
              className="text-slate-400 hover:text-white"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            <div className="bg-slate-800/50 p-4 rounded-lg border border-primary/20">
              <p className="text-xs text-primary uppercase font-bold mb-2">Evidencia Detectada</p>
              <p className="text-slate-300 text-sm whitespace-pre-wrap leading-relaxed italic">
                "{xrayContent}"
              </p>
            </div>

            <div className="text-xs text-slate-500 mt-4">
              <p>Esta evidencia fue extraída y utilizada por el Agente para generar el paso seleccionado.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



export default Results;
